import { useState, useContext, useEffect } from 'react';
import {
  GameStateContext,
  LinkRowRefContext,
} from '../../Providers/GameStates/GameState.jsx';
import { GameStatsContext } from '../../Providers/GameStats/GameStats.jsx';
import { Words } from '../../utils.jsx'
import { Box, useTheme } from '@mui/material';
import { customSnackbar } from '../../Hooks/CustomSnackbar.js';
import { getChangedIndex } from './RowChain.jsx';
import './letter-box.css'; // Keep for complex animations and keyframes

function Link() {
  const theme = useTheme()
  const [lastUpdatedIndex, setLastUpdatedIndex] = useState(-1)
  const { state, dispatch, goldenIndices } = useContext(GameStateContext)
  const { dispatch: dispatchStats } = useContext(GameStatsContext)
  const { lastKeyPressed, setLastKeyPressed } = useContext(GameStateContext)
  const [letters, setLetters] = useState(Array(state.wordLength).fill('')) // Initialize with empty strings based on wordLength
  const [cursorPos, setCursorPos] = useState(0) // Initialize with 0
  const { ref: linkRowRef, centerLinkDiv } = useContext(LinkRowRefContext)
  const { showErrorSnackbar } = customSnackbar()
  const completedUpperIndex = state.won
    ? getChangedIndex(state.upper[state.upper.length - 1]?.[0], state.link)
    : -1
  const completedLowerIndex = state.won
    ? getChangedIndex(state.link, state.lower[state.lower.length - 1]?.[0])
    : -1

  const handleWordSubmit = () => {
    const lastUpperWord = state.upper[state.upper.length - 1]
      ? state.upper[state.upper.length - 1][0]
      : null
    const firstLowerWord = state.lower[state.lower.length - 1][0]
      ? state.lower[state.lower.length - 1][0]
      : null
    const enteredWord = letters.join('').toLowerCase()

    if (lastUpperWord && firstLowerWord) {
      const validLength = Words.validLength(enteredWord, state.wordLength)
      if (validLength.result === 'error') {
        showErrorSnackbar(validLength.errorMsg)
        return
      }

      const validWord = Words.validWord(enteredWord, state.validWords)
      if (validWord.result === 'error') {
        showErrorSnackbar(validWord.errorMsg)
        return
      }

      const wordResult = Words.checkWord(
        lastUpperWord,
        enteredWord,
        firstLowerWord,
      )

      switch (wordResult.result) {
        case 'UPPER':
          dispatch({
            type: 'ADD_TO_UPPER',
            payload: {
              word: enteredWord,
              indexOfChange: wordResult.indexOfChange,
              linkRowRef,
            },
          })
          setLetters(Array(state.wordLength).fill(''))
          setCursorPos(0)
          break
        case 'LOWER':
          dispatch({
            type: 'ADD_TO_LOWER',
            payload: {
              word: enteredWord,
              indexOfChange: wordResult.indexOfChange,
              linkRowRef,
            },
          })
          setLetters(Array(state.wordLength).fill(''))
          setCursorPos(0)
          break
        case 'LINK':
          dispatch({ type: 'SET_LINK', payload: enteredWord })
          const { bestScore, avgScore } = state
          const score = state.upper.length + state.lower.length - 1
          dispatchStats({
            type: 'STREAK_UP',
            payload: { bestScore, avgScore, score },
          })
          break
        case 'error':
          showErrorSnackbar(wordResult.errorMsg)
          break
      }
      centerLinkDiv(linkRowRef)
    } else {
      showErrorSnackbar('Invalid game state. Please refresh and try again.')
    }
  }

  useEffect(() => {
    if (state.link !== '' && state.isInitialized) {
      setLetters(state.link.split(''));
      setCursorPos(state.wordLength);
    }
  }, [state.link, state.isInitialized, state.wordLength])

  useEffect(() => {
    if (state.won) {
      return
    }
    if (lastKeyPressed) {
      if (lastKeyPressed === 'Backspace') {
        if (cursorPos != 0) {
          setLetters((prevLetters) => {
            const newLetters = [...prevLetters]
            newLetters[cursorPos - 1] = ''
            return newLetters
          })
          setCursorPos((prevCursorPos) => prevCursorPos - 1)
        } else return
      } else if (lastKeyPressed === 'Enter') {
        handleWordSubmit() // or whatever the function is called
      } else if (
        /^[a-zA-Z]$/.test(lastKeyPressed) &&
        cursorPos !== state.wordLength
      ) {
        setLetters((prevLetters) => {
          const newLetters = [...prevLetters]
          newLetters[cursorPos] = lastKeyPressed
          return newLetters
        })
        setLastUpdatedIndex(cursorPos)
        setTimeout(() => {
          setLastUpdatedIndex(-1)
        }, 25)
        setCursorPos((prevCursorPos) => prevCursorPos + 1)
      }
      setLastKeyPressed(null)
    }
  }, [cursorPos, lastKeyPressed, handleWordSubmit, state.wordLength, state.won])

  useEffect(() => {
    setLetters(Array(state.wordLength).fill(''))
  }, [state.wordLength])

  return (
    <Box ref={linkRowRef} sx={theme.custom.letterRow}>
      {letters.map((letter, i) => (
        <Box
          key={i}
          sx={{
            ...theme.custom.letterBox, 
            backgroundColor:
              state.won == true
                ? theme.custom.tertiary
                : theme.palette.background.paper,
            border: `1px solid ${
              letter !== '' ? theme.palette.primary.main : theme.custom.border
            }`,
            color: theme.palette.text.primary,
            boxShadow:
              letter !== ''
                ? `0 10px 24px ${theme.palette.primary.main}25`
                : 'inset 0 0 0 1px rgba(127, 127, 127, 0.04)',
          }}
          className={`letter-box ${
            state.won
              ? 'letter-box--win-pop-in'
              : i === lastUpdatedIndex
              ? 'letter-box--pulse'
              : 'letter-box--initial-pop-in'
          } ${
            !state.won && goldenIndices.includes(i)
              ? 'letter-box--chain-node letter-box--chain-bridge'
              : ''
          } ${
            state.won && i === completedUpperIndex
              ? 'letter-box--chain-node letter-box--chain-bridge-upper'
              : ''
          } ${
            state.won && i === completedLowerIndex
              ? 'letter-box--chain-node letter-box--chain-bridge-lower'
              : ''
          }`}
        >
          {letter}
        </Box>
      ))}
    </Box>
  );
}

export default Link;
